import {
  ArrowLeftOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import TimeRangeInput from './TimeRangeInput';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../../api';
import { getLunarDate } from '../../utils/lunar';
import { getHolidayName } from '../../utils/holiday';
import { getScheduleTimezone, formatScheduleTime } from '../../utils/scheduleTime';
import NotificationHistory from './NotificationHistory';

const REMINDER_MINUTES_OPTIONS = [
  { value: 5, label: '5 分钟' },
  { value: 15, label: '15 分钟' },
  { value: 30, label: '30 分钟' },
  { value: 1440, label: '1 天' },
];
const REMINDER_CHANNELS_OPTIONS = [
  { value: 'email', label: '邮件' },
  { value: 'wecom', label: '企微' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const EVENT_BG_COLORS = [
  '#e6f7ff', '#f6ffed', '#fff7e6', '#fff1f0', '#f9f0ff', '#e6fffb',
  '#e0f2f1', '#fce4ec', '#f3e5f5', '#e8eaf6',
];

const getEventColor = (ev) => {
  const id = typeof ev.id === 'number' ? ev.id : (ev.course_name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return EVENT_BG_COLORS[Math.abs(id) % EVENT_BG_COLORS.length];
};

const formatEventTime = (timeStr) => {
  const tz = getScheduleTimezone();
  const time = tz ? dayjs(timeStr).tz(tz) : dayjs(timeStr);
  const hour = time.hour();
  const minute = time.minute();
  
  // 如果分钟为0，只显示小时；否则显示小时和分钟
  let formattedTime = '';
  if (hour === 0) {
    formattedTime = minute === 0 ? '12am' : `12:${minute.toString().padStart(2, '0')}am`;
  } else if (hour < 12) {
    formattedTime = minute === 0 ? `${hour}am` : `${hour}:${minute.toString().padStart(2, '0')}am`;
  } else if (hour === 12) {
    formattedTime = minute === 0 ? '12pm' : `12:${minute.toString().padStart(2, '0')}pm`;
  } else {
    const displayHour = hour - 12;
    formattedTime = minute === 0 ? `${displayHour}pm` : `${displayHour}:${minute.toString().padStart(2, '0')}pm`;
  }
  
  return formattedTime;
};

const getEventDisplayName = (ev, currentPersonId) => {
  // 获取参与者姓名（排除当前查看的人员）
  const participants = ev.participants || [];
  const otherParticipants = participants
    .filter((p) => p.user_id !== currentPersonId)
    .map((p) => p.user_real_name || p.user_name || '')
    .filter(Boolean);
  
  // 如果有其他参与者，显示第一个
  if (otherParticipants.length > 0) {
    return otherParticipants[0];
  }
  
  // 否则显示老师姓名
  return ev.teacher_name || '';
};

const formatEventText = (ev, currentPersonId) => {
  const time = formatEventTime(ev.start_time);
  const participantName = getEventDisplayName(ev, currentPersonId);
  const courseName = ev.course_name || '';
  
  if (participantName) {
    return `${time} ${participantName} ${courseName}`;
  }
  return `${time} ${courseName}`;
};

const SchedulePersonDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const personType = searchParams.get('role') || 'teacher';
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);

  const [calendarYear, setCalendarYear] = useState(dayjs().year());
  const [calendarMonth, setCalendarMonth] = useState(dayjs().month() + 1);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [listYear, setListYear] = useState(dayjs().year());
  const [listMonth, setListMonth] = useState(dayjs().month() + 1);
  const [listData, setListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('calendar');
  const [viewingSchedule, setViewingSchedule] = useState(null);
  const [viewingScheduleLoading, setViewingScheduleLoading] = useState(false);
  const [createMeetLoading, setCreateMeetLoading] = useState(false);
  const [dayEventsModal, setDayEventsModal] = useState({ open: false, date: null, events: [] });
  const [qaModalOpen, setQaModalOpen] = useState(false);
  const [defaultSettings, setDefaultSettings] = useState({ wecom_contact_sender_userid: '' });

  /** 将日期范围展开为 YYYY-MM-DD 数组 */
  const expandDateRange = (range) => {
    if (!range || !Array.isArray(range) || range.length < 2 || !range[0] || !range[1]) return [];
    const start = dayjs(range[0]).startOf('day');
    const end = dayjs(range[1]).startOf('day');
    if (start.isAfter(end)) return [];
    const list = [];
    for (let d = start; !d.isAfter(end); d = d.add(1, 'day')) list.push(d.format('YYYY-MM-DD'));
    return list;
  };

  /** 判断日期列表是否连续（可表示为一段范围） */
  const isConsecutiveDates = (dates) => {
    if (!dates || dates.length === 0) return false;
    const sorted = [...dates].filter(Boolean).map((s) => String(s).slice(0, 10)).sort();
    const uniq = [...new Set(sorted)];
    if (uniq.length !== sorted.length) return false;
    const start = dayjs(uniq[0]);
    const end = dayjs(uniq[uniq.length - 1]);
    const expectedCount = end.diff(start, 'day') + 1;
    return expectedCount === uniq.length && uniq.every((s, i) => dayjs(s).diff(start, 'day') === i);
  };

  const displayName = (row) =>
    row?.real_name || row?.username || row?.email || (row?.id ? `#${row.id}` : '—');

  const loadPerson = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/schedules/contacts/', {
        params: { role: personType, page: 1, page_size: 500 },
      });
      const list = res.data.results || [];
      const found = list.find((u) => String(u.id) === String(id));
      setPerson(found || null);
    } catch {
      setPerson(null);
    } finally {
      setLoading(false);
    }
  }, [id, personType]);

  useEffect(() => {
    loadPerson();
  }, [loadPerson]);

  // 获取默认配置
  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        const res = await axios.get('/api/schedules/default-settings/');
        setDefaultSettings(res.data || {});
      } catch (error) {
        console.error('Failed to fetch default settings:', error);
      }
    };
    fetchDefaultSettings();
  }, []);

  const loadTeachersAndStudents = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get('/api/schedules/contacts/', { params: { role: 'teacher', page_size: 500 } }),
        axios.get('/api/schedules/contacts/', { params: { role: 'student', page_size: 500 } }),
      ]);
      setTeachers(tRes.data.results || []);
      setStudents(sRes.data.results || []);
    } catch {
      setTeachers([]);
      setStudents([]);
    }
  }, []);

  const fetchCalendar = useCallback(async () => {
    if (!id) return;
    setCalendarLoading(true);
    try {
      const start = dayjs().year(calendarYear).month(calendarMonth - 1).date(1);
      const end = start.endOf('month');
      const res = await axios.get('/api/schedules/calendar/', {
        params: {
          person_id: id,
          start_date: start.format('YYYY-MM-DD'),
          end_date: end.format('YYYY-MM-DD'),
        },
      });
      setCalendarEvents(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCalendarEvents([]);
    } finally {
      setCalendarLoading(false);
    }
  }, [id, calendarYear, calendarMonth]);

  const fetchList = useCallback(async () => {
    if (!id) return;
    setListLoading(true);
    try {
      const res = await axios.get('/api/schedules/list/', {
        params: { person_id: id, year: listYear, month: listMonth },
      });
      setListData(Array.isArray(res.data) ? res.data : []);
    } catch {
      setListData([]);
    } finally {
      setListLoading(false);
    }
  }, [id, listYear, listMonth]);

  useEffect(() => {
    if (person) {
      fetchCalendar();
    }
  }, [person, fetchCalendar]);

  useEffect(() => {
    if (person) {
      fetchList();
    }
  }, [person, fetchList]);

  // 当 modal 打开且 students/teachers 数据加载完成后，更新表单值
  // 这确保 Select 组件能够正确显示学生/老师名称而不是 ID
  useEffect(() => {
    if (modalOpen && !editingId && person) {
      // 确保 students 和 teachers 列表已经加载，并且包含当前用户
      if (personType === 'student' && students.length > 0) {
        const currentStudent = students.find((s) => s.id === Number(id));
        if (currentStudent) {
          // 延迟一下确保 Select 组件已经渲染完成
          setTimeout(() => {
            form.setFieldsValue({
              participant_ids: [Number(id)],
            });
          }, 50);
        }
      } else if (personType === 'teacher' && teachers.length > 0) {
        const currentTeacher = teachers.find((t) => t.id === Number(id));
        if (currentTeacher) {
          setTimeout(() => {
            form.setFieldsValue({
              teacher_id: Number(id),
            });
          }, 50);
        }
      }
    }
  }, [modalOpen, editingId, person, personType, id, students, teachers, form]);

  const openCreate = async () => {
    await loadTeachersAndStudents();
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      time_slot_range: [dayjs().hour(14).minute(0).second(0).millisecond(0), dayjs().hour(15).minute(0).second(0).millisecond(0)],
      class_date_mode: 'range',
      class_date_range: null,
      class_dates: [],
      reminder_lead_minutes: [],
      reminder_channels: [],
      wecom_contact_sender_userid: defaultSettings.wecom_contact_sender_userid || '',
      participant_ids: person && personType === 'student' ? [Number(id)] : [],
      teacher_id: person && personType === 'teacher' ? person.id : undefined,
      frame_slug: 'https://framevr.io/republic-of-arts',
    });
    setModalOpen(true);
  };

  useEffect(() => {
    if (loading || !person || searchParams.get('openCreate') !== '1') return;
    openCreate();
    navigate(`/schedules/person/${id}?role=${personType}`, { replace: true });
  }, [loading, person, searchParams, id, personType, navigate]);

  const openView = async (ev) => {
    setViewingSchedule({ _loading: true, id: ev.id, course_name: ev.course_name });
    setViewingScheduleLoading(true);
    try {
      const res = await axios.get(`/api/schedules/${ev.id}/`);
      setViewingSchedule(res.data);
    } catch (e) {
      message.error(e.response?.data?.detail || '加载排课详情失败');
      setViewingSchedule(null);
    } finally {
      setViewingScheduleLoading(false);
    }
  };

  const handleCreateMeet = async (scheduleId) => {
    setCreateMeetLoading(true);
    try {
      const res = await axios.post(`/api/schedules/${scheduleId}/create-meet/`);
      const meetLink = res.data?.meet_link;
      if (meetLink) {
        message.success('Meet 会议链接已创建');
        const detailRes = await axios.get(`/api/schedules/${scheduleId}/`);
        setViewingSchedule(detailRes.data);
      }
    } catch (e) {
      message.error(e.response?.data?.detail || '创建 Meet 链接失败');
    } finally {
      setCreateMeetLoading(false);
    }
  };

  const isRealMeetLink = (link) => link && (link.startsWith('http://') || link.startsWith('https://'));

  const openEdit = async (record) => {
    setViewingSchedule(null);
    loadTeachersAndStudents();
    try {
      const res = await axios.get(`/api/schedules/${record.id}/`);
      const d = res.data;
      const hasDates = Array.isArray(d.class_dates) && d.class_dates.length > 0 && d.time_slot_start != null;
      const start = d.start_time ? dayjs(d.start_time) : null;
      const end = d.end_time ? dayjs(d.end_time) : null;
      const participantIds = (d.participants || []).map((p) => p.user_id);
      const timeStart = d.time_slot_start ? dayjs(d.time_slot_start, 'HH:mm:ss') : dayjs().hour(14).minute(0).second(0);
      const timeEnd = d.time_slot_end ? dayjs(d.time_slot_end, 'HH:mm:ss') : dayjs().hour(15).minute(0).second(0);
      const dates = d.class_dates || [];
      let classDateRange = null;
      let classDateMode = 'range';
      let classDatesList = [];
      if (hasDates && dates.length > 0) {
        if (isConsecutiveDates(dates)) {
          classDateMode = 'range';
          classDateRange = [dayjs(dates[0]), dayjs(dates[dates.length - 1])];
        } else {
          classDateMode = 'specific';
          classDatesList = [...dates].sort();
        }
      } else if (start && end) {
        classDateRange = [start.startOf('day'), end.startOf('day')];
      }
      form.setFieldsValue({
        course_name: d.course_name,
        time_slot_range: [timeStart, timeEnd],
        class_date_mode: classDateMode,
        class_date_range: classDateRange,
        class_dates: classDatesList,
        teacher_id: d.teacher_id,
        participant_ids: participantIds,
        reminder_lead_minutes: d.reminder_lead_minutes || [],
        reminder_channels: d.reminder_channels || [],
        wecom_contact_sender_userid: d.wecom_contact_sender_userid || '',
        meet_link: d.meet_link || '',
        frame_slug: d.frame_slug || '',
        status: d.status,
      });
      setEditingId(record.id);
      setModalOpen(true);
    } catch (e) {
      message.error(e.response?.data?.detail || '加载排课失败');
    }
  };

  const onFormFinish = async (values) => {
    setSubmitLoading(true);
    try {
      const range = values.time_slot_range;
      const payload = {
        course_name: values.course_name,
        teacher_id: values.teacher_id,
        participant_ids: values.participant_ids || [],
        reminder_lead_minutes: values.reminder_lead_minutes || [],
        reminder_channels: values.reminder_channels || [],
        wecom_contact_sender_userid: values.wecom_contact_sender_userid || null,
        meet_link: values.meet_link || null,
        frame_slug: (() => {
          const frameValue = (values.frame_slug || '').trim();
          if (!frameValue) return null;
          const urlMatch = frameValue.match(/framevr\.io\/([^\/\?]+)/);
          if (urlMatch) return urlMatch[1];
          return frameValue;
        })(),
        status: values.status,
        time_slot_start: range?.[0] ? range[0].format('HH:mm:ss') : null,
        time_slot_end: range?.[1] ? range[1].format('HH:mm:ss') : null,
        class_dates: values.class_date_mode === 'range'
        ? (expandDateRange(values.class_date_range) || [])
        : (values.class_dates && values.class_dates.length ? [...values.class_dates].sort() : []),
        start_time: null,
        end_time: null,
      };
      let res;
      if (editingId) {
        res = await axios.put(`/api/schedules/${editingId}/`, payload);
        message.success('已更新');
      } else {
        res = await axios.post('/api/schedules/', payload);
        message.success('已创建');
      }
      setModalOpen(false);
      fetchCalendar();
      fetchList();
      if (res?.data?.warnings?.length > 0) {
        const text = res.data.warnings.map((w) => `${w.user_display} 与「${w.other_course_name}」(${w.other_start}～${w.other_end}) 间隔较近（小于 10 分钟），请留意。`).join('\n');
        message.warning({ content: `课程已保存，但存在间隔较近的排课提醒：\n${text}`, duration: 8 });
      }
    } catch (e) {
      const err = e.response?.data;
      const msg = err?.non_field_errors?.[0] || err?.detail || (editingId ? '更新失败' : '创建失败');
      message.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const copyLaunchUrl = async (scheduleId) => {
    try {
      const res = await axios.get(`/api/schedules/${scheduleId}/launch-url/`);
      const url = res.data?.launch_url || '';
      if (!url) {
        message.warning('无启动链接');
        return;
      }
      const q = url.includes('?') ? url.substring(url.indexOf('?')) : '';
      const finalUrl = `${window.location.origin}/launch${q}`;
      await navigator.clipboard.writeText(finalUrl);
      message.success('启动链接已复制');
    } catch (e) {
      message.error(e.response?.data?.detail || '复制失败');
    }
  };

  const deleteSchedule = async (record) => {
    try {
      await axios.delete(`/api/schedules/${record.id}/`);
      message.success('已删除');
      fetchCalendar();
      fetchList();
    } catch (e) {
      message.error(e.response?.data?.detail || '删除失败');
    }
  };

  const byDay = {};
  calendarEvents.forEach((ev) => {
    const start = dayjs(ev.start_time);
    const end = dayjs(ev.end_time);
    const startDate = start.format('YYYY-MM-DD');
    const endDate = end.format('YYYY-MM-DD');
    for (let d = startDate; d <= endDate; d = dayjs(d).add(1, 'day').format('YYYY-MM-DD')) {
      if (!byDay[d]) byDay[d] = [];
      byDay[d].push(ev);
    }
  });

  const monthStart = dayjs().year(calendarYear).month(calendarMonth - 1).date(1);
  const monthEnd = monthStart.endOf('month');
  const daysInMonth = monthEnd.date();
  const startWeekday = monthStart.day();
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) {
    const d = monthStart.subtract(startWeekday - i, 'day');
    cells.push({ date: d, currentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: monthStart.date(d), currentMonth: true });
  }
  let next = 1;
  while (cells.length < totalCells) {
    cells.push({ date: monthEnd.add(next, 'day'), currentMonth: false });
    next += 1;
  }

  const statusMap = { draft: '草稿', published: '已发布', cancelled: '已取消' };

  const MARGIN = 12;
  const CALENDAR_MIN_HEIGHT = 'calc(100vh - 64px - 48px - 96px)';

  return (
    <div style={{ padding: MARGIN, background: '#fffbe9', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: MARGIN, flexShrink: 0 }}>
        <Space>
          <Tooltip title="返回列表">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/schedules')} />
          </Tooltip>
          <span style={{ fontSize: 18, fontWeight: 600 }}>{displayName(person)}</span>
        </Space>
        <Space>
          <Button icon={<QuestionCircleOutlined />} onClick={() => setQaModalOpen(true)}>帮助</Button>
          <Button icon={<SettingOutlined />} onClick={() => navigate('/schedules/settings')}>设置</Button>
          <Button type="primary" onClick={openCreate}>新建排课</Button>
        </Space>
      </div>
      <Spin spinning={loading}>
        {!loading && !person && (
          <Card>
            <Empty description="未找到该联系人" />
          </Card>
        )}
        {person && (
          <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: '12px 16px' } }}>
            <Tabs
              activeKey={activeTabKey}
              onChange={setActiveTabKey}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
              tabBarExtraContent={
                <Space size="small">
                  <span style={{ fontWeight: 600 }}>
                    {activeTabKey === 'calendar' ? `${calendarYear} 年 ${calendarMonth} 月` : `${listYear} 年 ${listMonth} 月`}
                  </span>
                  <Button
                    size="small"
                    onClick={() => {
                      if (activeTabKey === 'calendar') {
                        setCalendarMonth((m) => (m <= 1 ? 12 : m - 1));
                        setCalendarYear((y) => (calendarMonth <= 1 ? y - 1 : y));
                      } else {
                        setListMonth((m) => (m <= 1 ? 12 : m - 1));
                        setListYear((y) => (listMonth <= 1 ? y - 1 : y));
                      }
                    }}
                  >
                    上月
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      if (activeTabKey === 'calendar') {
                        setCalendarMonth((m) => (m >= 12 ? 1 : m + 1));
                        setCalendarYear((y) => (calendarMonth >= 12 ? y + 1 : y));
                      } else {
                        setListMonth((m) => (m >= 12 ? 1 : m + 1));
                        setListYear((y) => (listMonth >= 12 ? y + 1 : y));
                      }
                    }}
                  >
                    下月
                  </Button>
                </Space>
              }
              items={[
                {
                  key: 'calendar',
                  label: '日历',
                  children: (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
                      <Spin spinning={calendarLoading}>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gridTemplateRows: `auto repeat(${Math.ceil(totalCells / 7)}, 1fr)`,
                            gap: 1,
                            border: '1px solid #eee',
                            flex: 1,
                            minHeight: CALENDAR_MIN_HEIGHT,
                          }}
                        >
                          {['日', '一', '二', '三', '四', '五', '六'].map((h, colIndex) => (
                            <div
                              key={h}
                              style={{
                                padding: 8,
                                background: '#fafafa',
                                fontWeight: 600,
                                textAlign: 'center',
                              }}
                            >
                              {h}
                            </div>
                          ))}
                          {cells.map((c, i) => {
                            const key = c.date.format('YYYY-MM-DD');
                            const events = byDay[key] || [];
                            const colIndex = i % 7;
                            const localDate = new Date(c.date.year(), c.date.month(), c.date.date());
                            const lunarStr = getLunarDate(localDate);
                            const holidayStr = getHolidayName(localDate, lunarStr);
                            return (
                              <div
                                key={i}
                                style={{
                                  padding: 6,
                                  background: c.currentMonth ? '#fff' : '#fafafa',
                                  border: '1px solid #eee',
                                  minHeight: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  overflow: 'hidden',
                                }}
                              >
                                <div style={{ flexShrink: 0, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div style={{ flex: 1 }}>
                                    <span style={{ 
                                      color: c.currentMonth ? '#333' : '#999',
                                      fontSize: 14,
                                      fontWeight: 500,
                                    }}>
                                      {c.date.date()}
                                    </span>
                                    {lunarStr && (
                                      <div style={{ fontSize: 9, color: c.currentMonth ? '#666' : '#999', lineHeight: 1.1, marginTop: 1 }}>{lunarStr}</div>
                                    )}
                                    {holidayStr && (
                                      <div style={{ fontSize: 9, color: '#c41e3a', fontWeight: 500, lineHeight: 1.1, marginTop: 1 }}>{holidayStr}</div>
                                    )}
                                  </div>
                                  {events.length > 0 && (
                                    <div
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        background: '#1890ff',
                                        color: '#fff',
                                        fontSize: 10,
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        marginLeft: 4,
                                      }}
                                    >
                                      {events.length}
                                    </div>
                                  )}
                                </div>
                                <div style={{ 
                                  flex: 1, 
                                  overflowY: 'auto',
                                  overflowX: 'hidden',
                                  minHeight: 0,
                                }}>
                                  {events.slice(0, 3).map((ev) => (
                                    <div
                                      key={`${ev.id}-${key}`}
                                      style={{
                                        fontSize: 11,
                                        marginTop: 3,
                                        padding: '4px 6px',
                                        background: getEventColor(ev),
                                        borderRadius: 3,
                                        cursor: 'pointer',
                                        borderLeft: '3px solid rgba(0,0,0,0.2)',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.4,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                      onClick={() => openView(ev)}
                                      title={formatEventText(ev, id)}
                                    >
                                      {formatEventText(ev, id)}
                                    </div>
                                  ))}
                                  {events.length > 3 && (
                                    <div
                                      style={{
                                        fontSize: 11,
                                        marginTop: 3,
                                        padding: '4px 6px',
                                        background: '#f0f0f0',
                                        borderRadius: 3,
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        color: '#666',
                                        fontWeight: 500,
                                      }}
                                      onClick={() => setDayEventsModal({ open: true, date: c.date.format('YYYY-MM-DD'), events })}
                                    >
                                      ···
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Spin>
                    </div>
                  ),
                },
                {
                  key: 'list',
                  label: '当月排课列表',
                  children: (
                    <div style={{ overflow: 'auto', minHeight: 0 }}>
                      <Spin spinning={listLoading}>
                        <Table
                          rowKey="id"
                          size="small"
                          dataSource={listData}
                          columns={[
                            { title: '课程名', dataIndex: 'course_name', key: 'course_name' },
                            {
                              title: '开始时间',
                              key: 'start_time',
                              render: (_, r) => formatScheduleTime(r.start_time, 'YYYY-MM-DD HH:mm'),
                            },
                            {
                              title: '结束时间',
                              key: 'end_time',
                              render: (_, r) => formatScheduleTime(r.end_time, 'YYYY-MM-DD HH:mm'),
                            },
                            { title: '老师', dataIndex: 'teacher_name', key: 'teacher_name' },
                            {
                              title: '学生',
                              key: 'students',
                              render: (_, r) =>
                                (r.participants || [])
                                  .filter((p) => p.role === 'student')
                                  .map((p) => p.user_name)
                                  .join(', ') || '—',
                            },
                            {
                              title: '状态',
                              dataIndex: 'status',
                              key: 'status',
                              render: (s) => statusMap[s] || s,
                            },
                            {
                              title: '操作',
                              key: 'actions',
                              render: (_, r) => (
                                <Space>
                                  <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
                                  <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyLaunchUrl(r.id)}>复制链接</Button>
                                  <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({ title: '确认删除', onOk: () => deleteSchedule(r) })}>删除</Button>
                                </Space>
                              ),
                            },
                          ]}
                          pagination={false}
                        />
                      </Spin>
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        )}
      </Spin>

      <Drawer
        title={viewingSchedule?.course_name ?? '课程详情'}
        open={!!viewingSchedule}
        onClose={() => setViewingSchedule(null)}
        width={480}
        extra={
          viewingSchedule && !viewingSchedule._loading ? (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                openEdit(viewingSchedule);
                setViewingSchedule(null);
              }}
            >
              编辑
            </Button>
          ) : null
        }
      >
        {viewingSchedule?._loading || viewingScheduleLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : viewingSchedule ? (
          <Descriptions column={1} bordered size="small" labelStyle={{ width: 120, background: '#fafafa' }}>
            <Descriptions.Item label="课程名">{viewingSchedule.course_name}</Descriptions.Item>
            <Descriptions.Item label="状态">{statusMap[viewingSchedule.status] ?? viewingSchedule.status}</Descriptions.Item>
            <Descriptions.Item label="排课方式">按日期（一段日期 + 每天时间段）</Descriptions.Item>
            {Array.isArray(viewingSchedule.class_dates) && viewingSchedule.class_dates.length > 0 && viewingSchedule.time_slot_start != null ? (
              <>
                <Descriptions.Item label="每日时间">
                  {viewingSchedule.time_slot_start && viewingSchedule.time_slot_end
                    ? `${String(viewingSchedule.time_slot_start).slice(0, 5)} — ${String(viewingSchedule.time_slot_end).slice(0, 5)}`
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="上课日期">
                  {(viewingSchedule.class_dates || []).length
                    ? (viewingSchedule.class_dates || []).slice(0, 10).join('、') + ((viewingSchedule.class_dates || []).length > 10 ? ' …' : '')
                    : '—'}
                </Descriptions.Item>
              </>
            ) : (
              <>
                <Descriptions.Item label="开始时间">
                  {viewingSchedule.start_time ? formatScheduleTime(viewingSchedule.start_time, 'YYYY-MM-DD HH:mm') : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="结束时间">
                  {viewingSchedule.end_time ? formatScheduleTime(viewingSchedule.end_time, 'YYYY-MM-DD HH:mm') : '—'}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="老师">{viewingSchedule.teacher_name ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="学生">
              {(viewingSchedule.participants || [])
                .filter((p) => p.role === 'student')
                .map((p) => p.user_name)
                .join('、') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="提醒提前">
              {(viewingSchedule.reminder_lead_minutes || []).length ? (viewingSchedule.reminder_lead_minutes || []).join('、') + ' 分钟' : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="提醒渠道">
              {(viewingSchedule.reminder_channels || []).length ? (viewingSchedule.reminder_channels || []).join('、') : '—'}
            </Descriptions.Item>
            {viewingSchedule.wecom_contact_sender_userid && (
              <Descriptions.Item label="企微发送者">
                {viewingSchedule.wecom_contact_sender_userid}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Meet 链接或会议 ID">
              {viewingSchedule.meet_link || '—'}
              {!isRealMeetLink(viewingSchedule.meet_link) && (
                <Button
                  type="primary"
                  size="small"
                  loading={createMeetLoading}
                  style={{ marginLeft: 8 }}
                  onClick={() => handleCreateMeet(viewingSchedule.id)}
                >
                  创建 Meet 链接
                </Button>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="FrameVR 场景">{viewingSchedule.frame_slug || '—'}</Descriptions.Item>
            <Descriptions.Item label="一键启动">
              <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyLaunchUrl(viewingSchedule.id)}>
                复制启动链接
              </Button>
            </Descriptions.Item>
            <Descriptions.Item label="通知历史" span={1}>
              <NotificationHistory scheduleId={viewingSchedule.id} />
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Drawer>

      <Modal
        title={editingId ? '编辑排课' : '新建排课'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={560}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={onFormFinish}>
          <Form.Item name="course_name" label="课程名" rules={[{ required: true }]}>
            <Input placeholder="课程名" />
          </Form.Item>
          <Form.Item name="class_date_mode" label="上课日期" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="range">按范围（开始日期～结束日期）</Radio>
              <Radio value="specific">按具体日期（选择哪些天上课）</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.class_date_mode !== curr?.class_date_mode || (curr?.class_date_mode === 'specific' && (prev?.class_dates?.length !== curr?.class_dates?.length || (prev?.class_dates?.join?.() !== curr?.class_dates?.join?.())))}>
            {({ getFieldValue, setFieldValue }) => {
              const mode = getFieldValue('class_date_mode');
              if (mode === 'range') {
                return (
                  <Form.Item
                    name="class_date_range"
                    label="日期范围"
                    rules={[{ required: true, message: '请选择开始和结束日期' }, { type: 'array', len: 2, message: '请选择开始和结束日期' }]}
                  >
                    <DatePicker.RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={['开始日期', '结束日期']} />
                  </Form.Item>
                );
              }
              const classDates = getFieldValue('class_dates') || [];
              return (
                <Form.Item
                  name="class_dates"
                  label="具体日期"
                  rules={[{ required: true, type: 'array', min: 1, message: '请至少添加一个上课日期' }]}
                >
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <Space wrap>
                        {classDates.map((d) => (
                          <Tag
                            key={d}
                            closable
                            onClose={() => setFieldValue('class_dates', classDates.filter((x) => x !== d))}
                          >
                            {d}
                          </Tag>
                        ))}
                        {classDates.length === 0 && <span style={{ color: '#999', fontSize: 12 }}>请下方添加日期</span>}
                      </Space>
                    </div>
                    <Space wrap>
                      <DatePicker
                        format="YYYY-MM-DD"
                        placeholder="选择日期（可多次选择添加）"
                        onChange={(_, dateStr) => {
                          if (dateStr && !classDates.includes(dateStr)) {
                            setFieldValue('class_dates', [...classDates, dateStr].sort());
                          }
                        }}
                      />
                      <Button type="link" size="small" onClick={() => setFieldValue('class_dates', [])}>
                        清空
                      </Button>
                    </Space>
                  </div>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="time_slot_range" label="每天上课时间" rules={[{ required: true, type: 'array', len: 2, message: '请选择开始和结束时间' }]}>
            <TimeRangeInput />
          </Form.Item>
          <Form.Item name="teacher_id" label="老师" rules={[{ required: true }]}>
            <Select 
              placeholder="请选择老师" 
              allowClear 
              disabled={personType === 'teacher' && !editingId}
              options={teachers.map((t) => ({ value: t.id, label: displayName(t) }))} 
            />
          </Form.Item>
          <Form.Item name="participant_ids" label="参会人（学生等）">
            <Select 
              mode="multiple" 
              placeholder="选择学生" 
              allowClear 
              options={students.map((s) => ({ 
                value: s.id, 
                label: displayName(s),
                disabled: personType === 'student' && !editingId && s.id === Number(id)
              }))}
              onChange={(values) => {
                // 在学生角色下新建课程时，确保当前学生始终在列表中
                if (personType === 'student' && !editingId && person) {
                  const currentStudentId = Number(id);
                  if (!values || !values.includes(currentStudentId)) {
                    form.setFieldValue('participant_ids', [...(values || []), currentStudentId]);
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item name="reminder_lead_minutes" label="提醒提前">
            <Checkbox.Group options={REMINDER_MINUTES_OPTIONS} />
          </Form.Item>
          <Form.Item name="reminder_channels" label="提醒渠道">
            <Checkbox.Group options={REMINDER_CHANNELS_OPTIONS} />
          </Form.Item>
          <Form.Item 
            name="wecom_contact_sender_userid" 
            label="企微发送者UserID"
            tooltip="指定此课程企微通知的发送者（必须是已添加该客户的企微成员UserID），留空则使用默认配置或自动获取客户的跟进人"
          >
            <Input 
              placeholder={defaultSettings.wecom_contact_sender_userid || "留空使用默认配置"}
              allowClear
            />
          </Form.Item>
          <Form.Item label="Meet 链接或会议 ID" extra="请填写真实创建的 Google Meet 链接；留空则一键启动中不会打开 Meet。可点击「由系统创建」调用 Google 日历自动生成可用会议链接。">
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="meet_link" noStyle>
                <Input placeholder="留空可稍后在详情页点击「创建 Meet 链接」" />
              </Form.Item>
              {editingId && (
                <Button
                  type="default"
                  loading={createMeetLoading}
                  onClick={async () => {
                    setCreateMeetLoading(true);
                    try {
                      const res = await axios.post(`/api/schedules/${editingId}/create-meet/`);
                      if (res.data?.meet_link) {
                        form.setFieldValue('meet_link', res.data.meet_link);
                        message.success('Meet 会议链接已创建并填入');
                      }
                    } catch (e) {
                      message.error(e.response?.data?.detail || '创建失败');
                    } finally {
                      setCreateMeetLoading(false);
                    }
                  }}
                >
                  由系统创建
                </Button>
              )}
            </Space.Compact>
          </Form.Item>
          <Form.Item name="frame_slug" label="FrameVR 场景" extra="默认：https://framevr.io/republic-of-arts">
            <Input placeholder="https://framevr.io/republic-of-arts" />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="published">
            <Select options={[{ value: 'draft', label: '草稿' }, { value: 'published', label: '已发布' }, { value: 'cancelled', label: '已取消' }]} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitLoading}>{editingId ? '保存' : '创建'}</Button>
              <Button onClick={() => setModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`${dayEventsModal.date ? dayjs(dayEventsModal.date).format('YYYY年MM月DD日') : ''} 所有课程 (${dayEventsModal.events.length}个)`}
        open={dayEventsModal.open}
        onCancel={() => setDayEventsModal({ open: false, date: null, events: [] })}
        footer={[
          <Button key="close" onClick={() => setDayEventsModal({ open: false, date: null, events: [] })}>
            关闭
          </Button>,
        ]}
        width={600}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {dayEventsModal.events.length === 0 ? (
            <Empty description="暂无课程" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dayEventsModal.events.map((ev, idx) => (
                <div
                  key={`${ev.id}-${dayEventsModal.date}-${idx}`}
                  style={{
                    padding: '12px',
                    background: getEventColor(ev),
                    borderRadius: 6,
                    borderLeft: '4px solid rgba(0,0,0,0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => {
                    openView(ev);
                    setDayEventsModal({ open: false, date: null, events: [] });
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(4px)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                    {ev.course_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
                    时间：{formatScheduleTime(ev.start_time, 'HH:mm')} - {formatScheduleTime(ev.end_time, 'HH:mm')}
                  </div>
                  {ev.teacher_name && (
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
                      老师：{ev.teacher_name}
                    </div>
                  )}
                  {ev.participants && ev.participants.filter((p) => p.role === 'student').length > 0 && (
                    <div style={{ fontSize: 12, color: '#666' }}>
                      学生：{ev.participants
                        .filter((p) => p.role === 'student')
                        .map((p) => p.user_real_name || p.user_name)
                        .join('、')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
      <Modal
        title="排课系统使用指南"
        open={qaModalOpen}
        onCancel={() => setQaModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setQaModalOpen(false)}>
            我知道了
          </Button>,
        ]}
        width={800}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px 0' }}>
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#1890ff' }}>📋 排课系统功能</h3>
            <ul style={{ margin: 0, paddingLeft: 24, lineHeight: 2 }}>
              <li><strong>课程管理</strong>：创建、编辑、删除课程排课</li>
              <li><strong>日历视图</strong>：以日历形式查看当月所有课程安排</li>
              <li><strong>列表视图</strong>：以列表形式查看当月课程详情</li>
              <li><strong>Google 日历同步</strong>：自动同步课程到 Google 日历，并发送邀请</li>
              <li><strong>课程提醒</strong>：支持邮件、企业微信、WhatsApp 多渠道提醒</li>
              <li><strong>一键启动</strong>：生成启动链接，同时打开 Google Meet 和 FrameVR 虚拟教室</li>
              <li><strong>多角色支持</strong>：支持老师、学生不同角色的排课视图</li>
            </ul>
          </div>

          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#1890ff' }}>🚀 如何使用</h3>
            <ol style={{ margin: 0, paddingLeft: 24, lineHeight: 2 }}>
              <li><strong>创建课程</strong>：点击右上角"新建排课"按钮，填写课程信息</li>
              <li><strong>查看课程</strong>：在日历视图或列表视图中查看已创建的课程</li>
              <li><strong>编辑课程</strong>：点击课程卡片或列表中的"编辑"按钮修改课程信息</li>
              <li><strong>删除课程</strong>：在列表视图中点击"删除"按钮删除不需要的课程</li>
              <li><strong>复制启动链接</strong>：在课程详情或列表中点击"复制链接"获取一键启动链接</li>
              <li><strong>切换月份</strong>：使用日历视图右上角的"上月"/"下月"按钮切换查看不同月份的课程</li>
            </ol>
          </div>

          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#52c41a' }}>✅ 创建课程必填字段</h3>
            <ul style={{ margin: 0, paddingLeft: 24, lineHeight: 2 }}>
              <li><strong>课程名</strong> ⭐ <span style={{ color: '#ff4d4f' }}>（必填）</span></li>
              <li><strong>上课日期与时间</strong> ⭐ <span style={{ color: '#ff4d4f' }}>（必填）</span>：可「按范围」选开始～结束日期，或「按具体日期」勾选哪些天上课；再选每天上课时间</li>
              <li><strong>老师</strong> ⭐ <span style={{ color: '#ff4d4f' }}>（必填）</span></li>
            </ul>
          </div>

          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#722ed1' }}>📝 字段说明</h3>
            <div style={{ lineHeight: 2 }}>
              <div style={{ marginBottom: 12 }}>
                <strong>课程名</strong>：课程的名称标识，用于在日历和列表中显示
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>上课日期</strong>：支持两种方式 — <strong>按范围</strong>选择开始～结束日期（中间每天都上课），或<strong>按具体日期</strong>多次选择要上课的日期
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>每天上课时间</strong>：每天上课的时间段，如 14:00-15:00
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>老师</strong>：负责该课程的老师，必填
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>参会人（学生等）</strong>：参加课程的学生列表，可多选。在指定学生下新建课程时，该学生会自动加入且不可移除
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>提醒提前</strong>：课程开始前多久发送提醒，可选择：5分钟、15分钟、30分钟、1天
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>提醒渠道</strong>：通过哪些渠道发送提醒，可选择：邮件、企微、WhatsApp
                <div style={{ marginTop: 4, padding: 8, background: '#fff7e6', borderRadius: 4, fontSize: 12 }}>
                  💡 提示：用户需要在个人资料中配置对应的联系方式（邮箱、企业微信 UserID、WhatsApp 手机号）才能收到提醒
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>Meet 链接或会议 ID</strong>：请填写真实创建的 Google Meet 会议链接（完整 URL）
                <div style={{ marginTop: 4, padding: 8, background: '#e6f7ff', borderRadius: 4, fontSize: 12 }}>
                  💡 留空则一键启动中不会打开 Meet。同步到 Google 日历时可由系统自动创建会议并回填链接；也可手动粘贴从 meet.google.com 创建的链接
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>FrameVR 场景</strong>：FrameVR 虚拟教室的场景标识或完整 URL
                <div style={{ marginTop: 4, padding: 8, background: '#f6ffed', borderRadius: 4, fontSize: 12 }}>
                  💡 提示：默认值为 "https://framevr.io/republic-of-arts"，可输入完整 URL 或场景标识
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <strong>状态</strong>：课程的状态
                <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                  <li><strong>草稿</strong>：未发布的课程，不会同步到 Google 日历，不会发送提醒</li>
                  <li><strong>已发布</strong>：已发布的课程，会同步到 Google 日历，会根据设置发送提醒</li>
                  <li><strong>已取消</strong>：已取消的课程</li>
                </ul>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 24, padding: 12, background: '#fffbe6', borderRadius: 6, border: '1px solid #ffe58f' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#fa8c16' }}>⏰ 排课提醒定时任务</h3>
            <div style={{ lineHeight: 1.8, fontSize: 13 }}>
              <p style={{ margin: '0 0 8px 0' }}>
                排课提醒功能需要配置定时任务才能正常工作。定时任务会每分钟扫描需要发送提醒的课程，并按设置的渠道发送提醒。
              </p>
              <p style={{ margin: '8px 0', fontWeight: 600 }}>
                配置方法（服务器端）：
              </p>
              <pre style={{ 
                margin: '8px 0', 
                padding: '8px 12px', 
                background: '#fff', 
                borderRadius: 4, 
                fontSize: 12,
                overflowX: 'auto',
                border: '1px solid #d9d9d9'
              }}>
{`# 添加到 crontab（每分钟执行）
* * * * * cd /opt/ai-saas/docker && docker compose exec -T backend python manage.py send_schedule_reminders

# 或使用 --buffer-minutes 参数调整时间窗口
* * * * * cd /opt/ai-saas/docker && docker compose exec -T backend python manage.py send_schedule_reminders --buffer-minutes=2`}
              </pre>
              <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#666' }}>
                ⚠️ 注意：当前服务器可能未配置定时任务，请联系管理员检查 crontab 配置。
              </p>
            </div>
          </div>

          <div style={{ padding: 12, background: '#f0f5ff', borderRadius: 6, border: '1px solid #adc6ff' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#1890ff' }}>💡 使用技巧</h3>
            <ul style={{ margin: 0, paddingLeft: 24, lineHeight: 2 }}>
              <li>在日历视图中，点击日期上的课程卡片可查看课程详情</li>
              <li>如果某天课程太多显示不全，点击"···"可查看该天所有课程</li>
              <li>一键启动链接会自动打开 Google Meet 和 FrameVR，适合分享给学生快速进入课堂</li>
              <li>课程创建后会自动同步到 Google 日历，参会人会收到日历邀请</li>
              <li>在指定老师角色下新建课程时，该老师会自动选中且不可更改</li>
              <li>在指定学生角色下新建课程时，该学生会自动加入参会人列表且不可移除</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SchedulePersonDetail;
